import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import type {
  Appointment,
  Branch,
  Chair,
  HomeFigures,
  NoShowRisk,
  WaitlistEntry,
} from "@dentacore/shared-types";

import { listAppointments, listChairs } from "../api/appointments";
import { APPOINTMENTS_CHANGED } from "../api/appointments-changed";
import { listBranches } from "../api/branches";
import { readHomeFigures, readNoShowRisk } from "../api/home";
import { useActiveBranch } from "../api/use-active-branch";
import { useBranchTimezoneOrNull } from "../api/use-branch-timezone";
import { listWaitlist } from "../api/waitlist";
import { Badges, across, eachLocation, useShown } from "../shell/locations";
import { RING_ICONS } from "../shell/ring/ring-icons";
import { useRingProvider } from "../shell/ring/ring-registry";
import type { Located } from "../shell/locations";

/** Several locations' figures as one: counts added, amounts joined by currency. */
function addFigures(all: readonly HomeFigures[]): HomeFigures | null {
  const [first] = all;
  if (first === undefined) return null;
  const earliest = all
    .map((f) => f.openSlots.earliestAt)
    .filter((at): at is string => at !== null)
    .sort()[0];
  const owed = all.map((f) => f.paymentsToCollect).filter((p) => p !== null);
  const byCurrency = new Map<string, number>();
  for (const p of owed) {
    for (const m of p.outstanding)
      byCurrency.set(m.currency, (byCurrency.get(m.currency) ?? 0) + m.cents);
  }
  return {
    openSlots: {
      count: all.reduce((sum, f) => sum + f.openSlots.count, 0),
      earliestAt: earliest ?? null,
    },
    paymentsToCollect:
      owed.length === 0
        ? null
        : {
            count: owed.reduce((sum, p) => sum + p.count, 0),
            outstanding: [...byCurrency].map(([currency, cents]) => ({ currency, cents })),
            patients: owed.flatMap((p) => p.patients),
          },
  };
}
import { GoogleNudge } from "./google-nudge";
import { useAuth } from "../auth/auth-context";
import { useCan } from "../auth/use-can";
import { formatAmounts } from "../patients/money";
import { Modal } from "../ui/modal";
import { clockOf, figuresFor, longDate, today, upNext } from "./home-data";
import styles from "./home.module.css";
import { HomeTabs } from "./home-tabs";
import { RemainingToday } from "./remaining-today";
import { UpNext } from "./today-rail";
import { useLiveRefresh } from "../api/live";

/**
 * Home — today's dashboard, **one screen tall on any day** (decisions CS1–CS4).
 *
 * The client found it too crowded and too tall: on a busy practice the stacked
 * panels ran to one and a half screens. So, top to bottom:
 *
 *   - the greeting and **one summary line** carrying the four figures that
 *     used to be four tinted cards, each keeping what it did;
 *   - on the left, **the next patient**, then **Today** — the remaining list,
 *     scrolling inside its own box, with *Watch today*'s flags on the rows;
 *   - on the right, **one card with three tabs**: Needs you, Elsewhere,
 *     Attendance, each with its count.
 *
 * **Nothing was taken away** (CS1) — every figure, list, button and dialog the
 * old layout had is still here, in a box that scrolls or behind a tab that
 * says how much is in it. Where the window is too narrow or too short for one
 * screen, the boxes stack and the page scrolls as it always did.
 *
 * **No new backend.** Everything comes from `GET /appointments` for today,
 * `GET /home/figures`, the waitlist, the chairs, the branch and
 * `clinic_alerts` — endpoints the other screens already use.
 */
export function HomePage(): React.JSX.Element {
  const { user } = useAuth();
  const branchId = useActiveBranch();
  /**
   * The home currency, for a zero when nothing is owed. Every amount owed
   * arrives with its own currency (QA4-D6) and is shown in it, never added.
   */
  const currency = user?.currency ?? "USD";

  const [appointments, setAppointments] = useState<readonly Appointment[] | null>(null);
  const [waitlist, setWaitlist] = useState<readonly WaitlistEntry[]>([]);
  const [chairs, setChairs] = useState<readonly Chair[]>([]);
  const [branch, setBranch] = useState<Branch | null>(null);
  /**
   * The two figures the browser cannot work out for itself.
   *
   * **Its own request, and a silent failure.** It reaches the waitlist, the
   * chairs and the invoices, so folding it into the dashboard's own reload
   * would make one unreachable table blank the whole screen. Null renders zero
   * with the note still in place, which is honest: nothing was found.
   */
  const [computed, setComputed] = useState<HomeFigures | null>(null);
  /** Whether the *Payments to collect* detail is open (item 3). */
  const [showPayments, setShowPayments] = useState(false);
  /**
   * Today's patients who have been missing appointments (STORY-088).
   *
   * Its own request beside the figures, and silent on failure for the same
   * reason: a signal that could not be read must not blank the dashboard. An
   * empty list simply raises no no-show alerts, which is the honest fallback.
   */
  const [risks, setRisks] = useState<readonly NoShowRisk[]>([]);
  const [error, setError] = useState<string | null>(null);

  /**
   * **Today at the location, not at the browser** (P2 Q8). Null until the
   * location's zone is known, so the day's list is never asked for a guessed
   * day and then swapped.
   */
  const zone = useBranchTimezoneOrNull();
  // The locations ticked in the top bar: today across all of them.
  const places = useShown();
  const day = zone === null ? null : today(zone);
  const can = useCan();
  const readsWaitlist = can("waitlist.manage");

  /**
   * **Four lists, each on its own** (QA third run). They were one
   * `Promise.all`, so a Dental nurse or a Finance officer — who may not read
   * the waitlist — had the refusal throw away today's bookings, the chairs and
   * the location with it: Home read "0 of 0 remaining" over a day with a
   * critical-allergy patient booked. Only today's bookings are the screen; the
   * other three fall back quietly, and the waitlist is not asked for at all by
   * someone who cannot hold it.
   */
  const reload = useCallback(
    async (signal?: AbortSignal) => {
      if (day === null) return;
      const many = places.many;
      const [bookings, queue, rooms, branches] = await Promise.allSettled([
        many
          ? across(places.ids, () => listAppointments({ from: day, days: 1 }, signal)).then(
              (rows) => [...rows].sort((a, b) => a.startsAt.localeCompare(b.startsAt)),
            )
          : listAppointments({ from: day, days: 1 }, signal),
        readsWaitlist
          ? many
            ? across(places.ids, () => listWaitlist({ status: "WAITING" }, signal))
            : listWaitlist({ status: "WAITING" }, signal)
          : Promise.resolve([]),
        many ? across(places.ids, () => listChairs(signal)) : listChairs(signal),
        listBranches(signal),
      ]);
      if (signal?.aborted === true) return;

      setWaitlist(queue.status === "fulfilled" ? queue.value : []);
      if (rooms.status === "fulfilled") setChairs(rooms.value);
      if (branches.status === "fulfilled")
        setBranch(branches.value.find((row) => row.id === branchId) ?? null);

      if (bookings.status === "rejected") throw bookings.reason;
      setAppointments(bookings.value);
      setError(null);
    },
    [day, branchId, readsWaitlist, places.key],
  );
  // Today's list keeps up with the desk and WhatsApp (OQ-D19).
  useLiveRefresh(["appointments", "booking_requests", "waitlist_entries"], () => {
    void reload().catch(() => undefined);
  });

  useEffect(() => {
    // Null is "not yet", not "none" — see `useActiveBranch`. Three of the four
    // calls above are branch-scoped and would be refused without the header.
    if (branchId === null || day === null) return;

    const controller = new AbortController();

    reload(controller.signal).catch((cause: unknown) => {
      if (controller.signal.aborted) return;
      setAppointments([]);
      setError(
        cause instanceof Error && cause.message !== ""
          ? cause.message
          : "Could not read today's list.",
      );
    });

    return () => controller.abort();
  }, [branchId, day, reload]);

  // The quick ring moves a visit on from outside this screen (QR6).
  useEffect(() => {
    const onChanged = () => void reload().catch(() => undefined);
    window.addEventListener(APPOINTMENTS_CHANGED, onChanged);
    return () => window.removeEventListener(APPOINTMENTS_CHANGED, onChanged);
  }, [reload]);

  useEffect(() => {
    if (branchId === null || day === null) return;

    const controller = new AbortController();
    (places.many
      ? eachLocation(places.ids, () => readHomeFigures(day, controller.signal)).then(addFigures)
      : readHomeFigures(day, controller.signal)
    )
      .then(setComputed)
      .catch(() => {
        /* Silent: a secondary figure must not blank today's list. */
      });

    (places.many
      ? eachLocation(places.ids, () => readNoShowRisk(day, controller.signal)).then((all) =>
          all.flat(),
        )
      : readNoShowRisk(day, controller.signal)
    )
      .then(setRisks)
      .catch(() => {
        /* Silent, and empty — no signal rather than a false one. */
      });

    return () => controller.abort();
  }, [branchId, day, places.key]);

  const figures = figuresFor(appointments ?? []);
  /** The same booking *Up next* names, so the box and the summary agree. */
  const next = upNext(appointments ?? []);
  const timezone = zone ?? branch?.timezone ?? "UTC";
  const openSlots = computed?.openSlots.count ?? 0;
  const owed = computed?.paymentsToCollect?.count ?? 0;
  const navigate = useNavigate();

  /**
   * **Home's own ring, on blank space** (STORY-221, `page.home`). Each is a
   * button that already exists: *New appointment* is the calendar's (Home has
   * none of its own), *Add to waitlist* is the open-slots figure's way to the
   * waitlist, *Payments to collect* is that figure's dialog — offered, as the
   * figure is, only when somebody owes.
   */
  useRingProvider("page", () => ({
    ring: "page.home",
    title: "Today",
    meta: "Home",
    actions: [
      {
        key: "new",
        label: "New appointment",
        icon: RING_ICONS.book,
        needs: ["appointments.book"],
        hint: "Opens the booking form",
        run: () => navigate("/calendar?ring=new"),
      },
      {
        key: "wait",
        label: "Add to waitlist",
        icon: RING_ICONS.list,
        needs: ["waitlist.manage"],
        hint: "Opens the waitlist beside today",
        run: () => navigate("/calendar#waitlist"),
      },
      owed > 0 &&
        computed !== null &&
        computed.paymentsToCollect !== null && {
          key: "collect",
          label: "Payments to collect",
          icon: RING_ICONS.money,
          hint: "Who still owes money",
          run: () => setShowPayments(true),
        },
    ],
  }));

  return (
    <div className={styles.page} data-testid="home-page">
      <header className={styles.head}>
        <h1 className={styles.greeting}>
          {greeting()}, {user?.firstName}
        </h1>
        <p className={styles.date}>
          {day === null ? "" : longDate(day)}
          {places.many && ` · ${String(places.ids.length)} locations`}
        </p>
      </header>

      {/*
        Draws nothing unless this is an admin with a chair still waiting to be
        confirmed on Google, so Home is untouched for everyone else and for
        every practice that finished setting it up (STORY-130).
      */}
      <GoogleNudge />

      {error !== null && (
        <p className={styles.error} role="alert" data-testid="home-error">
          {error}
        </p>
      )}

      {/*
        **The four figures, as one line** (CS2) — each keeps its behaviour.
        *Patients remaining* names the booking it counts toward (item 2) and
        *Open slots* reaches today's calendar; *Payments to collect* opens who
        owes what (item 3); *Unconfirmed* stays a count, because a filtered
        view nobody has built would be a link that lands nowhere (F3). The
        explanatory notes of two cards ride in their titles (CS11).
      */}
      <div className={styles.summary} data-testid="home-summary">
        <Figure
          value={figures.remaining}
          label={`of ${String(figures.booked)} remaining`}
          tone="accent"
          testId="figure-remaining"
          to={next === null ? "/calendar" : `/calendar?appointment=${next.id}`}
        />
        <Figure
          value={figures.unconfirmed}
          label="unconfirmed"
          title="No arrival recorded yet"
          tone="warm"
          testId="figure-unconfirmed"
        />
        <Figure
          value={openSlots}
          label={`${openSlots === 1 ? "open slot" : "open slots"} · ${openSlotNote(computed, timezone)}`}
          tone="accent"
          testId="figure-open-slots"
          // Today's calendar with the waitlist open, to fill them (FX13).
          to="/calendar#waitlist"
        />
        <Figure
          value={owed}
          label={owed === 1 ? "payment to collect" : "payments to collect"}
          title="Unpaid issued invoices at this location"
          tone="good"
          testId="figure-payments"
          onOpen={owed === 0 ? undefined : () => setShowPayments(true)}
        />
      </div>

      <div className={styles.board}>
        <div className={styles.todayColumn}>
          <UpNext appointments={appointments} chairs={chairs} timezone={timezone} />
          <RemainingToday
            appointments={appointments}
            chairs={chairs}
            risks={risks}
            timezone={timezone}
            onChanged={() => void reload()}
            onError={setError}
            tag={(appointment) => (
              <Badges ats={[(appointment as Located<Appointment>).at]} shown={places} />
            )}
          />
        </div>

        <HomeTabs
          branchId={branchId}
          day={day ?? ""}
          currency={currency}
          payments={computed?.paymentsToCollect ?? null}
          onOpenPayments={() => setShowPayments(true)}
          waitlist={waitlist}
          chairs={chairs}
          timezone={timezone}
          onChanged={() => void reload()}
          onError={setError}
        />
      </div>

      {/*
        **What *Payments to collect* was counting** (client's manual QA, item
        3): the amount, the people, and an X — dismissable by clicking outside
        like every other dialog, which is what `Modal` supplies.
      */}
      {showPayments && computed !== null && computed.paymentsToCollect !== null && (
        <Modal
          label="Payments to collect"
          testId="payments-dialog"
          onClose={() => {
            setShowPayments(false);
          }}
        >
          <div className={styles.dialogHead}>
            <h3 className={styles.panelTitle}>Payments to collect</h3>
            <button
              type="button"
              className={styles.dialogClose}
              onClick={() => {
                setShowPayments(false);
              }}
              data-testid="payments-dialog-close"
              aria-label="Close"
            >
              ×
            </button>
          </div>

          <p className={styles.paymentsTotal} data-testid="payments-total">
            {formatAmounts(computed.paymentsToCollect.outstanding, currency)} from{" "}
            {computed.paymentsToCollect.count === 1
              ? "1 patient"
              : `${String(computed.paymentsToCollect.count)} patients`}{" "}
            with an unpaid invoice.
          </p>

          <ul className={styles.rows} data-testid="payments-list">
            {computed.paymentsToCollect.patients.map((owedRow) => (
              <li key={owedRow.patientId} className={styles.paymentRow}>
                <Link to={`/patients/${owedRow.patientId}`} className={styles.paymentName}>
                  {owedRow.name}
                </Link>
                <span className={styles.paymentAmount}>
                  {formatAmounts(owedRow.outstanding, currency)}
                </span>
              </li>
            ))}
          </ul>
        </Modal>
      )}
    </div>
  );
}

/**
 * One headline number, in the summary line.
 *
 * **`to` is optional, and only two have one** (decision F3); one opens a panel
 * in place with `onOpen`. A figure is a link or a button, never both, because
 * the two answer differently to a middle click and to a screen reader. **A
 * zero is quiet** — muted, so a day with nothing unconfirmed does not shout
 * the word at you — and it keeps its link, because "0 open slots" is still a
 * question about the calendar.
 */
function Figure({
  value,
  label,
  title,
  tone,
  testId,
  to,
  onOpen,
}: {
  value: number;
  label: string;
  /** The card's old note, where it explained rather than counted. */
  title?: string;
  tone: "accent" | "warm" | "good";
  testId: string;
  to?: string;
  onOpen?: () => void;
}): React.JSX.Element {
  const body = (
    <>
      <span className={styles.figureValue}>{value}</span> <span>{label}</span>
    </>
  );
  const props = {
    className: styles.figure,
    "data-tone": value === 0 ? "zero" : tone,
    "data-testid": testId,
    title,
  };

  if (onOpen !== undefined) {
    return (
      <button
        type="button"
        {...props}
        className={`${styles.figure} ${styles.figureLink}`}
        onClick={onOpen}
      >
        {body}
      </button>
    );
  }

  if (to === undefined) return <span {...props}>{body}</span>;

  return (
    <Link {...props} className={`${styles.figure} ${styles.figureLink}`} to={to}>
      {body}
    </Link>
  );
}

/**
 * The note on the open-slots figure.
 *
 * The mockup reads "15:00 — waitlist ready", so it names the time. With none to
 * offer it says which of the two reasons applies rather than a blank: an empty
 * waitlist and a full day are different problems, and the front desk acts on
 * them differently.
 */
function openSlotNote(computed: HomeFigures | null, timezone: string): string {
  if (computed === null) return "reading…";
  if (computed.openSlots.earliestAt === null) return "nothing the waitlist fits";
  return `${clockOf(computed.openSlots.earliestAt, timezone)}, waitlist ready`;
}

function greeting(): string {
  const hour = new Date().getHours();
  if (hour < 12) return "Good morning";
  if (hour < 18) return "Good afternoon";
  return "Good evening";
}
